/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useBuilder } from "@/hooks/useBuilder";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Edit,
  Trash2,
  Search,
  FileText,
  GripVertical,
  ArrowLeft,
  Copy,
  ToggleLeft,
  ToggleRight,
  EyeIcon,
  EyeOffIcon,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { format } from "date-fns";
import { PageData } from "@/types/builder";
import {
  fetchPages,
  createPage,
  updatePage,
  deletePage as deletePageApi,
} from "@/services/pagesApi";
import { fetchProject } from "@/services/projectsApi";
import { toast } from "@/hooks/use-toast";
import { log } from "console";
import { SelectRow } from "@/components/builder/property-editor";

interface PagesManagementProps {
  projectId?: string;
}

const PAGE_TYPES = [
  { value: "inner_page", label: "Infographic , forms" },
  { value: "offers_and_rewards", label: "Offers and Rewards" },
];

const PagesManagement = ({ projectId }: PagesManagementProps) => {
  const navigate = useNavigate();
  const { loadPage } = useBuilder();
  const [pages, setPages] = useState<PageData[]>([]);
  const [projectName, setProjectName] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<"name" | "createdAt" | "updatedAt">(
    "updatedAt",
  );
  const [sort_order, setsort_order] = useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [draggedPage, setDraggedPage] = useState<PageData | null>(null);
  const itemsPerPage = 10;

  // Create dialog states
  const [newPageName, setNewPageName] = useState("");
  const [newPageType, setNewPageType] = useState<string>(PAGE_TYPES[0].value);
  const [isCreating, setIsCreating] = useState(false);

  // Home page for layout editing
  const homePage = pages.find(
    (p) => p.page_type === "home" || p.title.toLowerCase() === "home",
  );

  const backPath = `/projects`;

  useEffect(() => {
    loadPages();
    if (projectId) {
      // loadProjectName();
    }
  }, [projectId]);

  const loadPages = async () => {
    try {
      setIsLoading(true);

      const data = await fetchPages(projectId);

      setPages(data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load pages",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filteredPages = pages
    .filter((page) => {
      // Show all pages including home page
      return page.title.toLowerCase().includes(searchTerm.toLowerCase());
    })
    .sort((a, b) => {
      // First sort by sort_order if both have it
      if (a.sort_order !== undefined && b.sort_order !== undefined) {
        return a.sort_order - b.sort_order;
      }

      const aValue = a[sortBy];
      const bValue = b[sortBy];

      if (sortBy === "name") {
        const aName = a.title;
        const bName = b.title;
        return sort_order === "asc"
          ? aName.localeCompare(bName)
          : bName.localeCompare(aName);
      }

      const aTime = new Date(aValue).getTime();
      const bTime = new Date(bValue).getTime();
      return sort_order === "asc" ? aTime - bTime : bTime - aTime;
    });

  const totalPages = Math.ceil(filteredPages.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const displayedPages = filteredPages.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  // When user confirms create in dialog, call this
  const handleCreatePageConfirmed = async (closeDialog?: () => void) => {
    if (!newPageName.trim()) {
      toast({ title: "Validation", description: "Page name is required" });
      return;
    }

    try {
      setIsCreating(true);
      const maxsort_order = pages.reduce(
        (max, p) => Math.max(max, p.sort_order || 0),
        0,
      );

      const newPage = await createPage({
        title: newPageName.trim(),
        components: [],
        projectId,
        sort_order: maxsort_order + 1,
        // store page type in a field - adjust backend if different
        page_type: newPageType,
      } as any);

      // Reset form
      setNewPageName("");
      setNewPageType(PAGE_TYPES[0].value);

      // Load and open the new page in editor
      loadPage(newPage._id);
      const queryParams = new URLSearchParams();
      if (projectId) queryParams.append("projectId", projectId);

      if (newPageType) queryParams.append("newPageType", newPageType);
      if (projectName) queryParams.append("projectName", projectName);

      navigate(
        `/builder/${newPage._id}${
          queryParams.toString() ? `?${queryParams.toString()}` : ""
        }`,
      );

      toast({
        title: "Success",
        description: "Page created and opened in editor",
      });

      // close the AlertDialog (if provided by the component usage)
      if (closeDialog) closeDialog();

      // Refresh pages list in background
      await loadPages();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create page",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleEditPage = (page: PageData) => {
    loadPage(page["_id"]);

    const queryParams = new URLSearchParams();
    if (projectId) queryParams.append("projectId", projectId);
    if (projectName) queryParams.append("projectName", projectName);
    navigate(
      `/builder/${page._id}${
        queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`,
    );
  };

  const handleDeletePage = async (pageId: string) => {
    try {
      await deletePageApi(pageId);
      await loadPages();
      toast({ title: "Success", description: "Page deleted successfully" });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete page",
        variant: "destructive",
      });
    }
  };

  const handleDuplicatePage = async (page: PageData) => {
    try {
      const maxSortOrder = pages.reduce(
        (max, p) => Math.max(max, p.sort_order || 0),
        0,
      );

      const duplicatedPage = await createPage({
        title: `${page.title} (Copy)`,
        components: page.components || [],
        projectId,
        sort_order: maxSortOrder + 1,
        page_type: page.page_type,
        settings: page.settings,
      } as any);

      await loadPages();
      toast({
        title: "Success",
        description: `Page duplicated as "${duplicatedPage.title}"`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to duplicate page",
        variant: "destructive",
      });
    }
  };

  const handleDragStart = (page: PageData) => {
    setDraggedPage(page);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (e: React.DragEvent, targetPage: PageData) => {
    e.preventDefault();
    if (!draggedPage || draggedPage._id === targetPage._id) return;

    const draggedIndex = filteredPages.findIndex(
      (p) => p._id === draggedPage._id,
    );
    const targetIndex = filteredPages.findIndex(
      (p) => p._id === targetPage._id,
    );

    if (draggedIndex === -1 || targetIndex === -1) return;

    // Reorder pages
    const newPages = [...filteredPages];
    newPages.splice(draggedIndex, 1);
    newPages.splice(targetIndex, 0, draggedPage);

    // Update sort_order for all affected pages
    const updatedPages = newPages.map((page, index) => ({
      ...page,
      sort_order: index,
    }));

    setPages(updatedPages);

    // Save the new orderpagebuilder_pages
    try {
      await Promise.all(
        updatedPages.map((page) =>
          updatePage(page._id, { sort_order: page.sort_order }),
        ),
      );
      toast({ title: "Success", description: "Page order updated" });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update page order",
        variant: "destructive",
      });
      await loadPages(); // Reload if failed
    }

    setDraggedPage(null);
  };

  const handleTogglePageStatus = async (page: PageData) => {
    const newStatus =
      (page as any).status === "published" ? "draft" : "published";

    try {
      await updatePage(page._id, { status: newStatus } as any);
      setPages((prev) =>
        prev.map((p) =>
          p._id === page._id ? ({ ...p, status: newStatus } as any) : p,
        ),
      );
      toast({
        title: "Success",
        description: `Page ${newStatus === "published" ? "published" : "draft"}`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update page status",
        variant: "destructive",
      });
    }
  };

  const handleParentPage = async (page: PageData, parentPageId: string) => {
    try {
      await updatePage(page._id, {
        parent_page: parentPageId,
      } as any);

      setPages((prev) =>
        prev.map((p) =>
          p._id === page._id
            ? {
                ...p,
                parent_page: parentPageId,
              }
            : p,
        ),
      );

      toast({
        title: "Success",
        description: `Page ${parentPageId} assigned as parent`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update page status",
        variant: "destructive",
      });
    }
  };

  const handleShowMenu = async (page: PageData, isEditing: boolean) => {
    try {
      await updatePage(page._id, { show_in_menu: isEditing } as any);
      setPages((prev) =>
        prev.map((p) =>
          p._id === page._id ? ({ ...p, show_in_menu: isEditing } as any) : p,
        ),
      );
      toast({
        title: "Success",
        description: `Page ${isEditing ? "added to menu" : "removed from menu"}`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update page status",
        variant: "destructive",
      });
    }
  };

  const getStatusBadge = (page: PageData) => {
    const isActive = (page as any).status !== "draft";
    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
          isActive ? "bg-primary/10 text-primary" : "bg-muted text-danger"
        }`}
      >
        {isActive ? "Published" : "Draft"}
      </span>
    );
  };

  return (
    <div className="">
      <div>
        <div className="container-fluid">
          <div className="social-dash-wrap">
            <div className="row">
              <div className="col-lg-12">
                <div className="breadcrumb-main">
                  <div className=" d-flex">
                    <Button
                      size="sm"
                      variant="shadow"
                      onClick={() => navigate(backPath)}
                      className="gap-2 mx-2 btn-icon btn-info"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      {/* Back */}
                    </Button>

                    <h4 className="text-capitalize breadcrumb-title align-center">
                      {projectName
                        ? `${projectName} - Pages`
                        : "Pages Management"}
                    </h4>
                  </div>

                  <div className="breadcrumb-action justify-content-center flex-wrap gap-2">
                    {/* Use AlertDialog as a creation dialog - trigger wraps the button */}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button className="gap-2">
                          <Plus className="w-4 h-4" />
                          Create Page
                        </Button>
                      </AlertDialogTrigger>

                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Create Page</AlertDialogTitle>
                          {/* <AlertDialogDescription>
                            Enter a name and select the page type to create a
                            new page.
                          </AlertDialogDescription> */}
                        </AlertDialogHeader>

                        <div className="card-body">
                          <div className="grid gap-4  ">
                            <div>
                              <label className="block text-sm font-medium mb-1">
                                Page Name
                              </label>
                              <Input
                                value={newPageName}
                                onChange={(e) => setNewPageName(e.target.value)}
                                placeholder="e.g. Home, Landing, Quiz"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium mb-1">
                                Page Type
                              </label>

                              <Select
                                value={newPageType}
                                onValueChange={(value) => {
                                  setNewPageType(value);
                                }}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select page type" />
                                </SelectTrigger>

                                <SelectContent style={{ zIndex: 9999 }}>
                                  {PAGE_TYPES.map((item) => (
                                    <SelectItem
                                      key={item.value}
                                      value={item.value}
                                    >
                                      {item.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>

                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>

                          {/* This matches the secondary outline button like 'Upload directly' */}

                          {/* Main action */}
                          <AlertDialogAction
                            onClick={() => handleCreatePageConfirmed()}
                            disabled={isCreating || !newPageName.trim()}
                            className="bg-primary btn-success"
                          >
                            {isCreating ? "Creating..." : "Create"}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-12">
                <div className="card">
                  <div className="card-body">
                    <div className="  mx-auto px-6 py-8">
                      {/* Table */}
                      <div className="bg-card rounded-lg border">
                        <Table>
                          <TableHeader className="border-bottom">
                            <TableRow>
                              <TableHead className="w-[50px]"></TableHead>
                              <TableHead className="w-[300px]">
                                Page Name
                              </TableHead>
                              {/* <TableHead>Components</TableHead> */}
                              {/* <TableHead>Date Created</TableHead> */}
                              <TableHead>Last Updated</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead>Active</TableHead>
                              <TableHead>Show in Menu</TableHead>
                              <TableHead>Select Parent</TableHead>
                              <TableHead className="text-right">
                                Actions
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {displayedPages.length === 0 ? (
                              <TableRow className="border-bottom">
                                <TableCell
                                  colSpan={8}
                                  className="text-center py-8"
                                >
                                  <div className="flex flex-col items-center gap-2">
                                    <FileText className="w-8 h-8 text-muted-foreground" />
                                    <p className="text-muted-foreground">
                                      {searchTerm
                                        ? "No pages match your search"
                                        : "No pages created yet"}
                                    </p>
                                    {!searchTerm && (
                                      <Button
                                        onClick={() => {
                                          // open dialog by programmatic trigger is not supported here,
                                          // ask the user to click Create Page button — but in most UI libs the trigger is clickable
                                        }}
                                        variant="outline"
                                        className="mt-2"
                                      >
                                        Create your first page
                                      </Button>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            ) : (
                              displayedPages.map((page, index) => (
                                <TableRow
                                  key={page._id}
                                  className={`border-bottom hover:bg-muted/50 ${
                                    index === 0 ? "" : "cursor-move"
                                  }`}
                                  draggable={index !== 0}
                                  onDragStart={
                                    index !== 0
                                      ? () => handleDragStart(page)
                                      : undefined
                                  }
                                  onDragOver={
                                    index !== 0 ? handleDragOver : undefined
                                  }
                                  onDrop={
                                    index !== 0
                                      ? (e) => handleDrop(e, page)
                                      : undefined
                                  }
                                >
                                  <TableCell>
                                    <GripVertical className="w-4 h-4 text-muted-foreground" />
                                  </TableCell>
                                  <TableCell className="font-medium">
                                    <div className="flex items-center gap-2">
                                      <FileText className="w-4 h-4 text-muted-foreground" />
                                      {page.title}
                                    </div>
                                  </TableCell>
                                  {/* <TableCell>
                                    <span className="text-muted-foreground">
                                      {page.components.length} components
                                    </span>
                                  </TableCell> */}
                                  {/* <TableCell>
                                    {format(
                                      new Date(page.updated_at),
                                      "MMM dd, yyyy",
                                    )}
                                  </TableCell> */}
                                  <TableCell>
                                    {page?.updated_at
                                      ? format(
                                          new Date(page.updated_at),
                                          "MMM dd, yyyy",
                                        )
                                      : ""}
                                  </TableCell>

                                  <TableCell>{getStatusBadge(page)}</TableCell>

                                  <TableCell>
                                    {page.page_type !== "home" && (
                                      <Switch
                                        checked={
                                          (page as any).status !== "draft"
                                        }
                                        onCheckedChange={() =>
                                          handleTogglePageStatus(page)
                                        }
                                      />
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {page.page_type !== "home" && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() =>
                                          handleShowMenu(
                                            page,
                                            !page.show_in_menu,
                                          )
                                        }
                                        className="h-8 w-8 p-0"
                                        title={
                                          page.show_in_menu
                                            ? "Hide from menu"
                                            : "Show in menu"
                                        }
                                      >
                                        {page.show_in_menu ? (
                                          <EyeIcon className="w-4 h-4" />
                                        ) : (
                                          <EyeOffIcon className="w-4 h-4" />
                                        )}
                                      </Button>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {page.page_type !== "home" && (
                                      <select
                                        className="form-control select2-style border rounded form-control-sm"
                                        style={{ width: "150px" }}
                                        value={page.parent_page || ""}
                                        disabled={page.show_in_menu === true}
                                        onChange={(e) =>
                                          handleParentPage(page, e.target.value)
                                        }
                                      >
                                        <option value="">Select Page</option>
                                        {pages
                                          .filter(
                                            (p) =>
                                              p._id !== page._id && // remove current page
                                              p.show_in_menu &&
                                              p.page_type !== "home", // remove home page
                                          )
                                          .map((p) => (
                                            <option key={p._id} value={p._id}>
                                              {p.title}
                                            </option>
                                          ))}
                                      </select>
                                    )}
                                  </TableCell>

                                  <TableCell className="text-right">
                                    <div className="flex items-center justify-end gap-2">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleEditPage(page)}
                                        className="h-8 w-8 p-0"
                                        title="Edit page"
                                      >
                                        <Edit className="w-4 h-4" />
                                      </Button>

                                      {/* Duplicate button - not for home page */}
                                      {page.page_type !== "home" && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() =>
                                            handleDuplicatePage(page)
                                          }
                                          className="h-8 w-8 p-0"
                                          title="Duplicate page"
                                        >
                                          <Copy className="w-4 h-4" />
                                        </Button>
                                      )}

                                      <AlertDialog>
                                        <AlertDialogTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                            disabled={page.page_type === "home"}
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </Button>
                                        </AlertDialogTrigger>

                                        <AlertDialogContent>
                                          <AlertDialogHeader>
                                            <AlertDialogTitle>
                                              Delete Page
                                            </AlertDialogTitle>
                                            <AlertDialogDescription>
                                              Are you sure you want to delete
                                              <b>"{page.title}"</b>? This action
                                              cannot be undone.
                                            </AlertDialogDescription>
                                          </AlertDialogHeader>

                                          <AlertDialogFooter>
                                            <AlertDialogCancel>
                                              Cancel
                                            </AlertDialogCancel>

                                            <AlertDialogAction
                                              onClick={() =>
                                                handleDeletePage(page._id)
                                              }
                                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                            >
                                              Delete
                                            </AlertDialogAction>
                                          </AlertDialogFooter>
                                        </AlertDialogContent>
                                      </AlertDialog>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </div>

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div className="flex items-center justify-between mt-6">
                          <p className="text-sm text-muted-foreground">
                            Showing {startIndex + 1} to
                            {Math.min(
                              startIndex + itemsPerPage,
                              filteredPages.length,
                            )}
                            of {filteredPages.length} pages
                          </p>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setCurrentPage((prev) => Math.max(prev - 1, 1))
                              }
                              disabled={currentPage === 1}
                            >
                              Previous
                            </Button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                              .filter(
                                (page) =>
                                  page === 1 ||
                                  page === totalPages ||
                                  Math.abs(page - currentPage) <= 1,
                              )
                              .map((page, index, array) => {
                                const showEllipsis =
                                  index > 0 && array[index - 1] !== page - 1;
                                return (
                                  <div key={page} className="flex items-center">
                                    {showEllipsis && (
                                      <span className="px-2 text-muted-foreground">
                                        ...
                                      </span>
                                    )}
                                    <Button
                                      variant={
                                        currentPage === page
                                          ? "default"
                                          : "outline"
                                      }
                                      size="sm"
                                      onClick={() => setCurrentPage(page)}
                                      className="w-8"
                                    >
                                      {page}
                                    </Button>
                                  </div>
                                );
                              })}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setCurrentPage((prev) =>
                                  Math.min(prev + 1, totalPages),
                                )
                              }
                              disabled={currentPage === totalPages}
                            >
                              Next
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* <div className="row">e</div> */}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PagesManagement;
